import * as React from 'react';
import {
  CheckmarkFillIcon,
  CloseEmptyIcon,
  ErrorIcon,
  InformationIcon,
  WarningIcon,
} from '@align-cpf/uikit-icons';
import { Icon } from '../Icon';
import { Link } from '../Link';
import { createLocators } from '@u1/locators';
import clsx from 'clsx';
import pauseable, { Timeout } from 'pauseable';
import useStyles from './Notification.styles';

export interface INotificationLink {
  href: string;
  label: React.ReactNode;
  target?: HTMLAnchorElement['target'];
}

export interface INotificationProps {
  /**
   * Notification variant
   * @default primary
   */
  type: 'information' | 'success' | 'warning' | 'error';
  /**
   * Title of the Notification
   */
  title: React.ReactNode;
  /**
   * Main message of the Notification
   */
  message: React.ReactNode;
  /**
   * Link object
   */
  link?: INotificationLink;
  /**
   * Time in ms, after which onClose will be invoked
   * set it to 0 to skip this behavior
   * @default 5000
   */
  delay?: number;
  /**
   * On close handler
   */
  multiline?: boolean;
  /**
   * Wether to color the background
   * of notification and make it fullWidth
   * @default false
   */
  inline?: boolean;
  /**
   * On close handler
   */
  onClose(): void;
}

export const notificationLocators = createLocators({
  name: 'Notification',
  componentLocators: [
    'notification',
    'body',
    'title',
    'message',
    'icon',
    'link',
    'close',
    'iconClose',
    'iconInformation',
    'iconCheckmarkFill',
    'iconWarning',
    'iconError',
  ],
});
const { locators } = notificationLocators();

const ICONS_MAP: Record<INotificationProps['type'], React.ReactNode> = {
  information: <Icon icon={InformationIcon} {...locators.iconInformation()} />,
  success: <Icon icon={CheckmarkFillIcon} {...locators.iconCheckmarkFill()} />,
  warning: <Icon icon={WarningIcon} {...locators.iconWarning()} />,
  error: <Icon icon={ErrorIcon} {...locators.iconError()} />,
};

const Notification: React.FC<INotificationProps> = ({
  onClose,
  delay = 5000,
  type = 'information',
  multiline: _multiline,
  title,
  message,
  link,
  inline,
}) => {
  const classes = useStyles();
  const timeout = React.useRef<Timeout>();

  React.useEffect(() => {
    if (delay) {
      timeout.current = pauseable.setTimeout(onClose, delay);
      return () => timeout.current?.clear();
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnter = () => timeout.current?.pause();
  const handleLeave = () => timeout.current?.resume();
  const multiline = inline || _multiline;

  return (
    <div
      {...locators.notification()}
      className={clsx(classes.root, classes[type], {
        [classes.multiline]: multiline,
        [classes.inline]: inline,
      })}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <div {...locators.body()} className={classes.body}>
        <div {...locators.icon({ type })} className={classes.icon}>
          {ICONS_MAP[type]}
        </div>
        <div className={classes.content}>
          <div {...locators.title()} className={classes.title}>
            {title}
          </div>
          <div
            {...locators.message()}
            className={clsx(classes.message, { [classes.withLink]: link })}
          >
            {message}
          </div>
          {link && (
            <Link
              locatorsOverrides={locators.link()}
              href={link.href}
              target={link.target}
            >
              {link.label}
            </Link>
          )}
        </div>

        {onClose && (
          <div
            {...locators.close()}
            className={classes.close}
            onClick={onClose}
          >
            <Icon
              icon={CloseEmptyIcon}
              locatorsOverrides={locators.iconClose()}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Notification;
